/**
 * Inspector — Hover/click handling
 * Shows overlay on hover, resolves key on click, opens sidebar.
 */

import { resolveKey, ResolvedKey } from './key-resolver.js';
import { parseBlockElement } from '../markup-parser.js';

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'TEXTAREA', 'NOSCRIPT', 'SVG']);

const BLOCK_SELECTOR =
  'p,h1,h2,h3,h4,h5,h6,li,td,th,label,button,figcaption,dt,dd,summary,caption';

export type OnSelectCallback = (resolved: ResolvedKey) => void;

export class Inspector {
  private overlay: HTMLDivElement;
  private currentTarget: Element | null = null;
  private onSelect: OnSelectCallback;
  private rafId: number | null = null;
  private active = true;
  private rootElement: Element;
  /** When set, clicks resolve keys from this map (snapshot viewer mode)
   *  instead of re-computing from the (redacted) DOM text. */
  private snapshotKeyMap: Map<number, string> | null;

  constructor(
    shadowRoot: ShadowRoot,
    onSelect: OnSelectCallback,
    rootElement?: Element,
    snapshotKeyMap?: Map<number, string>,
  ) {
    this.onSelect = onSelect;
    // Default scope is the live document body; snapshot viewer passes a
    // sandbox wrapper so blocks outside it are ignored.
    this.rootElement = rootElement || document.body;
    this.snapshotKeyMap = snapshotKeyMap || null;

    // Create overlay inside shadow DOM
    this.overlay = document.createElement('div');
    this.overlay.className = 'lr-overlay';
    shadowRoot.appendChild(this.overlay);

    // Bind handlers
    this.handleMouseOver = this.handleMouseOver.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleScroll = this.handleScroll.bind(this);

    // Listen on document (capture phase to get events before page handlers)
    document.addEventListener('mouseover', this.handleMouseOver, true);
    document.addEventListener('click', this.handleClick, true);
    window.addEventListener('scroll', this.handleScroll, true);
    window.addEventListener('resize', this.handleScroll);
  }

  destroy(): void {
    this.active = false;
    document.removeEventListener('mouseover', this.handleMouseOver, true);
    document.removeEventListener('click', this.handleClick, true);
    window.removeEventListener('scroll', this.handleScroll, true);
    window.removeEventListener('resize', this.handleScroll);
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.overlay.remove();
  }

  clearHighlight(): void {
    this.currentTarget = null;
    this.overlay.classList.remove('active');
  }

  /**
   * Suspend/resume the inspector. When inactive, hover/click handlers
   * early-return so page links navigate natively — this is what the toolbar's
   * "Browse" mode flips so a PM can click through the site's nav to reach
   * another page, instead of every click opening the edit drawer.
   */
  setActive(active: boolean): void {
    this.active = active;
    if (!active) this.clearHighlight();
  }

  isActive(): boolean {
    return this.active;
  }

  private handleMouseOver(e: Event): void {
    if (!this.active) return;
    const target = e.target as Element;
    if (!target || !target.closest) return;

    // Skip editor UI elements (inside shadow DOM won't trigger this, but be safe)
    if (target.closest('#__lionrapid-editor')) return;

    // Skip anything outside the scope root (snapshot viewer mode)
    if (!this.rootElement.contains(target)) return;

    // Skip non-translatable elements
    if (this.shouldSkip(target)) {
      this.clearHighlight();
      return;
    }

    // Find nearest block element
    const block = target.closest(BLOCK_SELECTOR) || target.closest('[data-lr-block]');
    if (!block) {
      this.clearHighlight();
      return;
    }

    if (block === this.currentTarget) return;

    this.currentTarget = block;
    this.positionOverlay(block);
  }

  private handleClick(e: Event): void {
    if (!this.active) return;
    const target = e.target as Element;
    if (!target || !target.closest) return;

    // Skip editor UI
    if (target.closest('#__lionrapid-editor')) return;

    // Skip anything outside the scope root (snapshot viewer mode)
    if (!this.rootElement.contains(target)) return;

    // Find the block to resolve
    const block = target.closest(BLOCK_SELECTOR) || target.closest('[data-lr-block]');
    if (!block) return;

    if (this.shouldSkip(block)) return;

    e.preventDefault();
    e.stopPropagation();

    // Snapshot viewer mode: look up pre-computed key by data-lr-snap-idx
    // instead of re-running resolveKey on the redacted text.
    if (this.snapshotKeyMap) {
      const idxAttr = block.getAttribute('data-lr-snap-idx');
      if (idxAttr !== null) {
        const idx = parseInt(idxAttr, 10);
        const key = this.snapshotKeyMap.get(idx);
        if (key) {
          const unit = parseBlockElement(block);
          this.onSelect({ key, unit, translatedUnit: null, element: block });
          return;
        }
      }
      // Block has no snap-idx — not a pre-extracted block, skip
      return;
    }

    const resolved = resolveKey(block);
    if (resolved) {
      this.onSelect(resolved);
    }
  }

  private handleScroll(): void {
    if (!this.currentTarget) return;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      if (this.currentTarget) {
        this.positionOverlay(this.currentTarget);
      }
    });
  }

  private positionOverlay(element: Element): void {
    const rect = element.getBoundingClientRect();
    this.overlay.style.top = `${rect.top}px`;
    this.overlay.style.left = `${rect.left}px`;
    this.overlay.style.width = `${rect.width}px`;
    this.overlay.style.height = `${rect.height}px`;
    this.overlay.classList.add('active');
  }

  private shouldSkip(element: Element): boolean {
    // Skip SKIP_TAGS
    let current: Element | null = element;
    while (current) {
      if (SKIP_TAGS.has(current.tagName)) return true;
      if (current.hasAttribute('data-no-translate')) return true;
      if (current.getAttribute('translate') === 'no') return true;
      current = current.parentElement;
    }
    return false;
  }
}
